import type { Editor } from "@tiptap/core";
import { getHost } from "./host";

export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;
export const MAX_IMAGE_BYTES = 25 * 1024 * 1024;

export function checkImage(file: File): string | null {
  const name = file.name || "This file";
  if (!(IMAGE_TYPES as readonly string[]).includes(file.type)) return `${name} is not a PNG, JPEG, WebP or GIF image.`;
  if (file.size > MAX_IMAGE_BYTES) return `${name} is larger than 25 MB.`;
  return null;
}

export function imageFiles(dt: DataTransfer | null): File[] {
  return dt ? Array.from(dt.files).filter((f) => f.type.startsWith("image/")) : [];
}

export function dataUrlToFile(dataUrl: string, name = "pasted-image"): File | null {
  const m = /^data:(image\/(png|jpeg|webp|gif));base64,([A-Za-z0-9+/=\s]+)$/i.exec(dataUrl);
  if (!m?.[1] || !m[2] || !m[3]) return null;
  let bin: string;
  try {
    bin = atob(m[3].replace(/\s/g, ""));
  } catch {
    return null;
  }
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  const ext = m[2].toLowerCase() === "jpeg" ? "jpg" : m[2].toLowerCase();
  return new File([bytes], `${name}.${ext}`, { type: m[1].toLowerCase() });
}

const reason = (e: unknown) => (e instanceof Error ? e.message : "unknown error");

/**
 * Uploads each valid image (one at a time; the API client queues and retries a busy server) and inserts a klImage
 * at `pos` (or at the selection). Returns how many were inserted. Nothing is inserted once the editor is read-only
 * (lost lock while an upload was in flight).
 */
export async function insertImages(editor: Editor, files: File[], pos: number | null): Promise<number> {
  const host = getHost(editor);
  let inserted = 0;
  let at = pos;
  for (const file of files) {
    if (!editor.isEditable || editor.isDestroyed) break;
    const problem = checkImage(file);
    if (problem) {
      host.notify(problem, "error");
      continue;
    }
    try {
      const { src } = await host.uploadImage(file);
      if (!editor.isEditable || editor.isDestroyed) break;
      const node = { type: "klImage", attrs: { src } };
      const chain = editor.chain().focus();
      (at === null
        ? chain.insertContent(node)
        : chain.insertContentAt(Math.min(at, editor.state.doc.content.size), node)
      ).run();
      // Later images in the same drop follow the first one instead of piling onto the same spot.
      at = null;
      inserted += 1;
    } catch (e) {
      host.notify(`${file.name} could not be uploaded: ${reason(e)}`, "error");
    }
  }
  return inserted;
}

export function pickImageFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = IMAGE_TYPES.join(",");
    input.addEventListener("change", () => resolve(input.files?.[0] ?? null), { once: true });
    input.addEventListener("cancel", () => resolve(null), { once: true });
    input.click();
  });
}
